// Lote E: PDF de un caso (datos, aviso al IESS, medidas correctivas y evidencias) y resumen anual de casos, avisos y
// medidas. Mismo estilo que los PDF de inspecciones y del plan (portada oscura, títulos azules, campos en dos columnas,
// encabezado y pie). Todo lo que se escribe aquí lo ve el cliente: sin rutas ni nombres internos.

import { jsPDF } from 'jspdf';
import { paraPdf } from '../pe/pe-plan-pdf';
import { etiquetaNivel } from './inc-datos';
import {
  ETIQUETA_AVISO, ETIQUETA_MEDIDA, NORMA_AVISO, NOTA_PLAZO_MEDIDAS, estadoAvisoCaso, estadoMedida, estadoMedidasCaso, tipoCaso,
  type CasoInc, type MedidaInc,
} from './inc-estado';
import { NOTA_TERMINO, NOTA_TERMINO_ENFERMEDAD, fechaCorta } from './inc-plazos';

export const TITULO_CASO = 'Informe del caso: aviso al IESS y medidas correctivas';
export const TITULO_RESUMEN = 'Resumen anual de casos, avisos al IESS y medidas correctivas';

export interface CasoPDF extends CasoInc {
  description: string;
  severity?: string | null;
  trabajador?: string | null;
  puesto?: string | null;
  lugar?: string | null;
  tipoEtiqueta: string;
  investigacion?: boolean;          // hay informe de investigación (Resolución C.D. 513)
  disease_risk_agent?: string | null;
  disease_physician?: string | null;
  origen?: string | null;           // etiqueta del origen de la presunción
  iess_constancia_name?: string | null;
}
export interface MedidaPDF extends MedidaInc {
  description: string;
  control_level: string | null;
  responsable: string;
  follow_up: string | null;
  evidence_name: string | null;
  completed_at: string | null;
  reopen_reason?: string | null;
}
export interface DatosCasoPDF {
  empresa: string;
  caso: CasoPDF;
  medidas: MedidaPDF[];
  imagenes: Record<string, string | null>;   // por medida (y 'aviso'): data URL de la imagen; null = PDF o no se pudo incrustar
  hoy: string;
  fecha: Date;
}
export interface DatosResumenPDF {
  empresa: string;
  anio: number;
  casos: CasoPDF[];
  medidas: MedidaPDF[];
  hoy: string;
  fecha: Date;
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22;
const formatoImagen = (dataUrl: string) => (dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG');

function lienzo(tituloDoc: string, empresa: string, subtitulo: string) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const T = (s: string) => paraPdf(s);
  let y = ARRIBA;
  const espacio = (alto: number) => { if (y + alto > ALTO_UTIL) { doc.addPage(); y = ARRIBA; } };
  const texto = (s: string, tam = 9, o: { negrita?: boolean; sangria?: number; gris?: boolean; color?: [number, number, number] } = {}) => {
    doc.setFont('helvetica', o.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (o.color) doc.setTextColor(...o.color); else if (o.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    const sangria = o.sangria ?? 0;
    (doc.splitTextToSize(T(s), ANCHO - sangria) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN + sangria, y); y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const titulo = (s: string) => {
    espacio(14); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const COL_ETIQUETA = 58, COL_VALOR = 62, LINEA = 4.2;
  const campos = (pares: [string, string][]) => {
    for (const [k, v] of pares) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      const etiqueta = doc.splitTextToSize(T(`${k}:`), COL_ETIQUETA) as string[];
      doc.setFont('helvetica', 'normal');
      const valor = doc.splitTextToSize(T(v || '—'), ANCHO - COL_VALOR) as string[];
      const lineas = Math.max(etiqueta.length, valor.length);
      espacio(lineas * LINEA + 2);
      const y0 = y;
      doc.setFont('helvetica', 'bold');
      etiqueta.forEach((l, n) => doc.text(l, MARGEN, y0 + n * LINEA));
      doc.setFont('helvetica', 'normal');
      valor.forEach((l, n) => doc.text(l, MARGEN + COL_VALOR, y0 + n * LINEA));
      y += (lineas - 1) * LINEA + 5.2;
    }
  };
  const imagen = (img: string | null | undefined, nombre: string | null | undefined) => {
    const lado = 40;
    espacio(lado + 4);
    doc.setDrawColor(203, 213, 225); doc.rect(MARGEN + 4, y, lado, lado);
    if (img) { try { doc.addImage(img, formatoImagen(img), MARGEN + 4.5, y + 0.5, lado - 1, lado - 1); } catch { /* imagen dañada: queda el recuadro */ } }
    else { doc.setFontSize(7); doc.setTextColor(100, 116, 139); doc.text(T('Archivo adjunto'), MARGEN + 8, y + lado / 2); doc.setTextColor(15, 23, 42); }
    doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    doc.text(T(nombre || ''), MARGEN + lado + 8, y + 4);
    doc.setTextColor(15, 23, 42);
    y += lado + 4;
  };
  // Portada
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 60, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
  (doc.splitTextToSize(T(tituloDoc), ANCHO) as string[]).forEach((l, n) => doc.text(l, MARGEN, 26 + n * 7));
  doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
  doc.text(T(empresa || 'Empresa'), MARGEN, 44);
  doc.setFontSize(10); doc.text(T(subtitulo), MARGEN, 52);
  doc.setTextColor(15, 23, 42);
  y = 72;
  const cerrar = (pie: string) => {
    const paginas = doc.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      if (p > 1) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
        doc.text(T(empresa || ''), MARGEN, 10);
        doc.text(T(tituloDoc.length > 60 ? `${tituloDoc.slice(0, 57)}...` : tituloDoc), 210 - MARGEN, 10, { align: 'right' });
        doc.setDrawColor(226, 232, 240); doc.line(MARGEN, 12, 210 - MARGEN, 12);
      }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text(T(pie), MARGEN, 290);
      doc.text(T(`Página ${p} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
      doc.setTextColor(15, 23, 42);
    }
    return doc;
  };
  return { doc, texto, titulo, campos, imagen, cerrar, espacio };
}

function lineasAviso(c: CasoPDF, hoy: string): [string, string][] {
  const a = estadoAvisoCaso(c, hoy);
  return [
    ['Fecha límite del aviso', `${fechaCorta(a.fechaLimite)} (10 días hábiles)`],
    ['Estado del aviso', ETIQUETA_AVISO[a.estado]],
    ['Número de aviso', c.iess_report_number || '—'],
    ['Fecha de envío', fechaCorta(c.iess_report_date)],
    ['Constancia', c.iess_constancia_path ? (c.iess_constancia_name || 'Subida') : 'Sin constancia'],
  ];
}

export function generarCasoPDF(d: DatosCasoPDF): jsPDF {
  const c = d.caso;
  const enfermedad = tipoCaso(c.incident_type) === 'enfermedad';
  const L = lienzo(TITULO_CASO, d.empresa, `${c.tipoEtiqueta} · ${fechaCorta(c.incident_date)}`);
  L.titulo('1. Datos del caso');
  L.campos([
    ['Tipo', c.tipoEtiqueta],
    [enfermedad ? 'Fecha en que se advirtieron los indicios' : 'Fecha del siniestro', fechaCorta(c.incident_date)],
    ['Trabajador', c.trabajador || '—'], ['Puesto', c.puesto || '—'], ['Lugar', c.lugar || '—'],
    ...(enfermedad ? [['Agente o factor de riesgo', c.disease_risk_agent || '—'], ['Médico', c.disease_physician || '—'],
      ['Origen', c.origen || '—']] as [string, string][] : [['Gravedad', c.severity || '—']] as [string, string][]),
    ['Descripción', c.description],
    ['Informe de investigación', c.investigacion ? 'Registrado' : 'No registrado'],
  ]);
  L.titulo('2. Aviso al IESS');
  L.campos(lineasAviso(c, d.hoy));
  L.texto(enfermedad ? NOTA_TERMINO_ENFERMEDAD : NOTA_TERMINO, 7.5, { gris: true });
  L.texto(`Base: ${NORMA_AVISO}. StorSafe no envía el aviso: registra la constancia del aviso presentado en línea.`, 7.5, { gris: true });
  if (c.iess_constancia_path) L.imagen(d.imagenes.aviso, c.iess_constancia_name);
  L.titulo('3. Medidas correctivas');
  const mc = estadoMedidasCaso(c, d.medidas, d.hoy);
  L.campos([['Estado de las medidas', mc.estado === 'cumple' ? 'Cumple' : mc.estado === 'no_cumple' ? 'No cumple'
    : `En proceso (plazo para definir medidas hasta el ${fechaCorta(mc.finPlazo)})`]]);
  L.texto(NOTA_PLAZO_MEDIDAS, 7.5, { gris: true });
  if (d.medidas.length === 0) L.texto('El caso no tiene medidas correctivas registradas.', 9, { color: [185, 28, 28] });
  d.medidas.forEach((m, n) => {
    const e = estadoMedida(m, d.hoy);
    L.texto(`${n + 1}. ${m.description}`, 9, { negrita: true });
    L.campos([
      ['Jerarquía de controles', etiquetaNivel(m.control_level)], ['Responsable', m.responsable || '—'],
      ['Fecha compromiso', fechaCorta(m.due_date)], ['Estado', ETIQUETA_MEDIDA[e]],
      ['Seguimiento', m.follow_up || '—'],
      ...(e === 'cerrada' ? [['Fecha de cierre', fechaCorta(m.completed_at)]] as [string, string][] : []),
      ...(m.reopen_reason ? [['Motivo de la última reapertura', m.reopen_reason]] as [string, string][] : []),
    ]);
    if (m.evidence_path) L.imagen(d.imagenes[m.id], m.evidence_name);
  });
  return L.cerrar(`${TITULO_CASO} · ${fechaCorta(c.incident_date)} · generado ${d.fecha.toLocaleDateString('es-EC')}`);
}

export function generarResumenAnualPDF(d: DatosResumenPDF): jsPDF {
  const delAnio = d.casos.filter(c => c.incident_date.startsWith(String(d.anio)) && tipoCaso(c.incident_type)).sort((a, b) => a.incident_date.localeCompare(b.incident_date));
  const L = lienzo(TITULO_RESUMEN, d.empresa, `Año ${d.anio}`);
  const ids = new Set(delAnio.map(c => c.id));
  const medidas = d.medidas.filter(m => ids.has(m.incident_id));
  const avisos = delAnio.map(c => estadoAvisoCaso(c, d.hoy).estado);
  const estados = medidas.map(m => estadoMedida(m, d.hoy));
  L.titulo('1. Totales');
  L.campos([
    ['Accidentes', String(delAnio.filter(c => tipoCaso(c.incident_type) === 'accidente').length)],
    ['Presunciones de enfermedad profesional', String(delAnio.filter(c => tipoCaso(c.incident_type) === 'enfermedad').length)],
    ['Avisos con constancia', String(avisos.filter(a => a === 'con_constancia' || a === 'fuera_de_termino').length)],
    ['Avisos fuera de término', String(avisos.filter(a => a === 'fuera_de_termino').length)],
    ['Sin aviso, dentro del término', String(avisos.filter(a => a === 'en_termino').length)],
    ['Vencidos sin aviso', String(avisos.filter(a => a === 'vencido').length)],
    ['Medidas correctivas', String(medidas.length)],
    ['Cerradas con evidencia', String(estados.filter(e => e === 'cerrada').length)],
    ['Vencidas sin cierre', String(estados.filter(e => e === 'vencida').length)],
    ['Casos sin ninguna medida', String(delAnio.filter(c => !medidas.some(m => m.incident_id === c.id)).length)],
  ]);
  L.titulo('2. Casos');
  if (delAnio.length === 0) L.texto('No hay accidentes ni presunciones de enfermedad profesional en el año.');
  delAnio.forEach((c, n) => {
    const a = estadoAvisoCaso(c, d.hoy);
    const suyas = medidas.filter(m => m.incident_id === c.id);
    const cerradas = suyas.filter(m => estadoMedida(m, d.hoy) === 'cerrada').length;
    L.texto(`${n + 1}. ${fechaCorta(c.incident_date)} · ${c.tipoEtiqueta}${c.trabajador ? ` · ${c.trabajador}` : ''}`, 9, { negrita: true });
    L.texto(`Aviso: ${ETIQUETA_AVISO[a.estado]} (límite ${fechaCorta(a.fechaLimite)}${c.iess_report_number ? `, número ${c.iess_report_number}` : ''}). `
      + `Medidas: ${suyas.length} (${cerradas} cerradas con evidencia).`, 8.5, { sangria: 4 });
  });
  L.texto(`${NOTA_TERMINO} Base: ${NORMA_AVISO}.`, 7.5, { gris: true });
  L.texto(NOTA_PLAZO_MEDIDAS, 7.5, { gris: true });
  return L.cerrar(`${TITULO_RESUMEN} · ${d.anio} · generado ${d.fecha.toLocaleDateString('es-EC')}`);
}
